/*
* Developed for G.J. Gardner Homes by Softeq Development Corporation
* http://www.softeq.com
*/

import { Component, Inject, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getGtoEntryKey } from '@gh/builder-shared';
import { GraphicalTakeOffPictureRestService } from '@gh/builder-shared-data';
import { GoogleDocument } from '@gh/common-internal-shared';
import { ID } from '@gh/common-shared-data';
import { isMessageBasket } from '@gh/core-messages';
import {
  createFileInfoFromBlob,
  FileInvalidEvent,
  InvalidFileReason,
  NotificationService,
  SingleUploaderComponent,
  UploadContext,
  UploadItem,
  UploadSlot, validationError,
  WaitingService,
} from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { mergeMap$ } from '@gh/rx/operators';
import { isNil } from 'lodash';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';
import {
  GraphicalTakeOffBusinessService,
  GraphicalTakeOffHousePlanView,
  GraphicalTakeOffPicture,
} from '../../resources';

/**
 * Detect if file is PDF.
 * NOTE: This is quick and dirty detection method.
 *       We can use header-inspecting method from FileReader API if needed.
 *
 * @param {Blob} file - File
 * @returns {boolean} - Is file PDF?
 */
const isFilePdf = (file: UploadItem): boolean => !!file.mimeType.match('pdf');

/**
 * Import view component
 */
@Component({
  selector: 'gh-graphical-take-off-import',
  template: require('./graphical-take-off-import.component.html'),
  styles: [require('./graphical-take-off-import.component.scss')],
  providers: [GraphicalTakeOffBusinessService, UploadContext],
})
export class GraphicalTakeOffImportComponent {
  /**
   * Upload slot
   *
   * @type {UploadSlot}
   */
  uploadSlot: UploadSlot;

  /**
   * Preview rotation angle
   *
   * @type {number}
   */
  rotation: number = 0;

  /**
   * Preview zoom value
   *
   * @type {number}
   */
  zoomScale: number = 0.5;

  /**
   * File name.
   * It will be assigned to house plan view
   *
   * @type {string}
   */
  fileNameControl = new FormControl();

  /**
   * File name max length
   *
   * @type {boolean}
   */
  fileNameMaxLength: number = 32;

  /**
   * Is component in PDF mode?
   *
   * @type {boolean}
   */
  pdfMode: boolean = false;

  /**
   * Number of PDF pages
   *
   * @type {number}
   */
  pdfPages: number = 1;

  /**
   * Current PDF page
   *
   * @type {number}
   */
  pdfPage: number = 1;

  /**
   * PDF source
   *
   * @type {string}
   */
  pdfSrc?: string;

  /**
   * Image source
   *
   * @type {string}
   */
  imgSrc?: string;

  /**
   * Newly created view
   *
   * @type {GraphicalTakeOffHousePlanView}
   */
  view: GraphicalTakeOffHousePlanView;

  gFolderId: ID;

  views: GraphicalTakeOffHousePlanView[];
  newViews: GraphicalTakeOffHousePlanView[] = [];

  @ViewChild(SingleUploaderComponent, { static: false }) uploader: SingleUploaderComponent;

  private previewLoaded = false;
  private isUploadCompleted = false;

  /**
   * Constructor
   *
   * @param {any} data - Dialog data
   * @param {MatDialogRef<GtoImportComponent>} dialogRef - Dialog ref
   * @param {UploadContext} uploadContext - Upload context
   * @param {WaitingService} waitingService - Waiting service
   * @param {NotificationService} notificationService - Notification service
   * @param pictureRest
   * @param {GraphicalTakeOffBusinessService} gtoService - GTO business service
   * @returns {void}
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<GraphicalTakeOffImportComponent>,
    private uploadContext: UploadContext,
    private waitingService: WaitingService,
    private notificationService: NotificationService,
    private pictureRest: GraphicalTakeOffPictureRestService,
    private gtoService: GraphicalTakeOffBusinessService,
  ) {
    this.initUploader();
    this.views = data.views;
    this.gFolderId = data.gFolderId;
  }

  get fileName(): string {
    return this.fileNameControl.value;
  }

  /**
   * File name error
   *
   * @type {boolean}
   */
  get fileNameError(): boolean {
    return this.fileNameMaxLengthError;
  }

  /**
   * File name max length error
   *
   * @type {boolean}
   */
  get fileNameMaxLengthError(): boolean {
    return this.fileName ? this.fileName.length > this.fileNameMaxLength : false;
  }

  get isPdfLoaded(): boolean {
    return this.pdfMode && this.previewLoaded;
  }

  /**
   * Get PDF page numbers
   *
   * @returns {number[]} - PDF page numbers
   */
  get pages(): number[] {
    return Array.from(Array(this.pdfPages).keys())
      .map((el: any, i: number) => i + 1);
  }

  /**
   * Is PDF page selection disabled?
   *
   * @returns {boolean} - Is PDF page selection disabled?
   */
  get pageSelectDisabled(): boolean {
    return !this.isPdfLoaded || 1 === this.pdfPages;
  }

  /**
   * Is PDF previous page disabled?
   *
   * @returns {boolean} - Is PDF previous page disabled?
   */
  get prevPageDisabled(): boolean {
    return !this.isPdfLoaded || 1 === this.pdfPage;
  }

  /**
   * Is PDF next page disabled?
   *
   * @returns {boolean} - Is PDF next page disabled?
   */
  get nextPageDisabled(): boolean {
    return !this.isPdfLoaded || this.pdfPages === this.pdfPage;
  }

  /**
   * Is preview options disabled?
   *
   * @returns {boolean}
   */
  get optionsDisabled(): boolean {
    return !this.pdfSrc && !this.imgSrc;
  }

  /**
   * Is import disabled?
   *
   * @returns {boolean} - Is import disabled?
   */
  get importDisabled(): boolean {
    return !this.isUploadCompleted;
  }

  /**
   * Zoom values.
   * Calculates array from 0.1 to 1 with step equals to 0.1
   *
   * @returns {number[]} - Zoom values
   */
  get zoomValues(): number[] {
    const zoomLength = 10;
    return Array.from(Array(zoomLength).keys())
      .map((el: any, i: number) => (i + 1) / zoomLength);
  }

  /**
   * Format zoom value
   *
   * @param {number} value - Current value
   * @returns {number} - New value
   */
  getZoomValue(value: number): number {
    const percent = 100;
    return value * percent;
  }

  /**
   * On PDF load complete
   * See: https://github.com/VadimDez/ng2-pdf-viewer#after-load-complete
   *
   * @param {PDFDocumentProxy} pdf - PDF proxy object
   * @returns {void}
   */
  pdfLoadComplete(pdf: PDFDocumentProxy): void {
    this.pdfPages = pdf.numPages;
    this.previewLoaded = true;
  }

  imageLoadComplete(): void {
    this.previewLoaded = true;
  }

  /**
   * Show previous PDF page
   *
   * @returns {void}
   */
  prevPage(): void {
    this.pdfPage--;
  }

  /**
   * Show next PDF page
   *
   * @returns {void}
   */
  nextPage(): void {
    this.pdfPage++;
  }

  /**
   * Rotate preview left (CCW)
   *
   * @returns {void}
   */
  rotateLeft(): void {
    const angle = 90;
    this.rotation = this.rotation - angle;
  }

  /**
   * Rotate preview right (CW)
   *
   * @returns {void}
   */
  rotateRight(): void {
    const angle = 90;
    this.rotation = this.rotation + angle;
  }

  /**
   * Zoom preview
   *
   * @param {number} scale - Zoom scale
   * @returns {void}
   */
  zoom(scale: number): void {
    this.zoomScale = scale;
  }

  /**
   * Close import dialog
   *
   * @returns {void}
   */
  close(): void {
    this.dialogRef.close(this.newViews);
  }

  /**
   * Import view.
   * Get image from PDF file if component is in PDF mode and
   * then create new house plan view
   *
   * @returns {void}
   */
  importView(): void {
    const item = this.uploadSlot.item;
    if (!item || !item.blob || this.fileNameError) {
      this.fileNameControl.markAsTouched();
      return;
    }

    if (this.pdfMode) {
      this.gtoService
        .createPicture(item['key'], this.pdfPage).pipe(
        this.waitingService.operator(),
        mergeMap$((picture: GraphicalTakeOffPicture) => {
          item['key'] = picture.s3Key;
          return of$(item);
        }))
        .subscribe(() => this.createView());
    } else {
      this.createView();
    }
  }

  /**
   * Invalid file handler
   *
   * @param {FileInvalidEvent} event - Event
   * @returns {void}
   */
  onFileInvalid(event: FileInvalidEvent): void {
    // @ts-ignore
    this.notificationService.error({
      [InvalidFileReason.TooLarge]: 'msg_gto_item_picture_not_correct',
      [InvalidFileReason.NotAccepted]: 'msg_gto_item_picture_not_accepted',
    }[event.reason]);
  }

  onUploadItemChange(item?: UploadItem): void {
    if (isNil(item)) {
      this.resetItem();
    } else {
      this.setItem(item);
    }
  }

  onUploadCompleted(): void {
    this.isUploadCompleted = true;
  }

  onDocumentSelected(docs: GoogleDocument[]): void {
    const doc = docs[0];

    this.pictureRest.downloadFromGDrive(doc.id)
      .pipe(this.waitingService.operator())
      .subscribe(
        (blob) => {
          const item = this.uploadContext.create(createFileInfoFromBlob(doc.name, blob, doc.mimeType));
          this.setItem(item);
          this.uploadSlot.assign(item);
          this.uploadSlot.start();
        },
        this.notificationService.processAll);
  }

  /**
   * Create view and close dialog
   *
   * @returns {void}
   */
  private createView(): void {
    const item = this.uploadSlot.item;
    if (!item || !item.blob) {
      return;
    }

    const hpView: GraphicalTakeOffHousePlanView = <any>{
      name: this.fileName,
      corners: [],
      picture: {
        key: item['key'],
        name: item.name,
      },
    };

    const id = getGtoEntryKey(this.data.entryPoint, 'id');
    hpView[id] = this.data.id;

    this
      .gtoService
      .createHousePlanView(
        this.data.id,
        hpView,
        this.data.entryPoint,
      ).pipe(
      this.waitingService.operator())
      .subscribe(
        (view: GraphicalTakeOffHousePlanView) => {
          this.newViews.push(view);
          this.view = view;
          this.resetItem();
          this.notificationService.success('msg_gto_uploaded_successfully');
        },
        (errors) => {
          if (isMessageBasket(errors) && errors.has('msg_gto_view_name_not_unique')) {
            this.fileNameControl.setErrors({ unique: validationError('msg_gto_view_name_not_unique') });
            this.fileNameControl.markAsTouched();
          }
          this.notificationService.processAll(errors);
        });
  }

  /**
   * Handle PDF file
   *
   * @param {Blob} file - File
   * @returns {void}
   */
  private previewPdf(file: Blob): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.pdfSrc = e.target.result;
    };

    reader.readAsArrayBuffer(file);
  }

  /**
   * Handle image file
   *
   * @param {Blob} file - File
   * @returns {void}
   */
  private previewImage(file: Blob): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imgSrc = e.target.result;
    };

    reader.readAsDataURL(file);
  }

  /**
   * Init uploader
   *
   * @returns {void}
   */
  private initUploader(): void {
    this.uploadSlot = this.uploadContext.createUploadSlot({
      entryPoint: 'TEMPORARY',
    });
  }

  private setItem(item: UploadItem): void {
    this.imgSrc = void 0;
    this.pdfSrc = void 0;
    this.previewLoaded = false;
    this.isUploadCompleted = false;

    const file = item.blob;
    if (!file) {
      return;
    }

    this.fileNameControl.setValue(item.name);

    this.pdfMode = isFilePdf(item);
    if (this.pdfMode) {
      this.previewPdf(file);
    } else {
      this.previewImage(file);
    }
  }

  /**
   * Reset component
   *
   * @returns {void}
   */
  private resetItem(): void {
    this.uploader.reset();
    this.fileNameControl.setValue('');
    this.previewLoaded = false;
    this.isUploadCompleted = false;

    this.rotation = 0;
    this.zoomScale = 0.5; // tslint:disable-line:no-magic-numbers

    this.pdfMode = false;
    this.pdfPages = 1;
    this.pdfPage = 1;

    this.pdfSrc = void 0;
    this.imgSrc = void 0;
  }
}
